// Coloration syntaxique des exercices INTERACTIFS de Reps (find_error,
// tap_target, drop_blocks, fill_blank). Ces quatre-là ne peuvent pas utiliser
// Monaco : ils découpent le code en lignes ou en segments cliquables, et
// Monaco ne rend qu'un bloc d'un seul tenant. Ils avaient donc leur propre
// coloration, écrite pour le HTML uniquement : du CSS, du JavaScript ou du
// Python s'y affichaient en gris.
//
// Ajouter un langage = ajouter une entrée dans LANGS ci-dessous. Rien d'autre
// à toucher : les quatre exercices passent tous par tokenize().
//
// Le module est MIROIR de reps-app/src/features/lesson/highlight.ts (app
// mobile) : toute règle ajoutée ici doit l'être là-bas, sinon la même leçon
// n'a pas les mêmes couleurs sur les deux fronts.

// Familles de jetons. Teintes de Monaco vs-dark, pour rester cohérent avec
// l'éditeur des exercices d'écriture, et classes Tailwind adaptatives pour les
// fragments posés sur fond clair (fill_blank).
export const TOKEN_DARK = {
    tag: "#569cd6",
    attr: "#9cdcfe",
    str: "#ce9178",
    comment: "#6a9955",
    kw: "#c586c0",
    num: "#b5cea8",
    prop: "#9cdcfe",
    sel: "#d7ba7d",
    fn: "#dcdcaa",
};

export const TOKEN_LIGHT = {
    tag: "text-sky-600 dark:text-sky-300",
    attr: "text-violet-600 dark:text-violet-300",
    str: "text-emerald-600 dark:text-emerald-300",
    comment: "text-gray-400 dark:text-gray-500",
    kw: "text-fuchsia-600 dark:text-fuchsia-300",
    num: "text-amber-600 dark:text-amber-300",
    prop: "text-violet-600 dark:text-violet-300",
    sel: "text-sky-600 dark:text-sky-300",
    fn: "text-yellow-600 dark:text-yellow-300",
};

// Règles par langage, ESSAYÉES DANS L'ORDRE à chaque position : la première
// qui accroche gagne. D'où l'ordre commentaires -> chaînes -> le reste, sinon
// un mot-clé à l'intérieur d'une chaîne serait colorié.
//
// Le 4e élément borne la règle à une PORTÉE : "tag" ne l'autorise qu'entre
// `<balise` et son `>`. Sans lui, l'apostrophe de « L'équipe » écrite dans le
// texte d'une page ouvrait une chaîne, refermée à l'apostrophe suivante :
// six lignes de HTML passaient en orange, et l'apprenant lisait une erreur de
// coloration là où son code était juste.
const LANGS = {
    html: [
        ["comment", /<!--[\s\S]*?-->/y],
        ["tag", /<\/?[a-zA-Z][\w-]*/y],
        ["tag", /\/?>/y],
        ["attr", /[a-zA-Z][\w-]*(?=\s*=)/y, null, "tag"],
        ["str", /"[^"]*"|'[^']*'/y, null, "tag"],
    ],
    css: [
        ["comment", /\/\*[\s\S]*?\*\//y],
        ["str", /"[^"]*"|'[^']*'/y],
        ["at", /@[a-zA-Z-]+/y, "kw"],
        ["fn", /[a-zA-Z-]+(?=\()/y],
        // Propriété : un identifiant suivi de deux-points DANS un bloc. Le
        // sélecteur, lui, précède une accolade ou une virgule.
        ["prop", /[a-zA-Z-]+(?=\s*:)/y],
        ["sel", /[.#]?[a-zA-Z][\w-]*(?=\s*[,{])/y],
        ["num", /#[0-9a-fA-F]{3,8}|\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw|ms|s|deg|fr)?/y],
        // Valeur nommée (red, gray, bold, inherit...) : ce qui reste après le
        // sélecteur, la propriété et la fonction est une valeur. Sans cette
        // règle, `color: gray;` n'aurait de couleur que sur `color`.
        ["val", /[a-zA-Z][\w-]*/y, "str"],
    ],
    javascript: [
        ["comment", /\/\/[^\n]*|\/\*[\s\S]*?\*\//y],
        ["str", /"[^"\n]*"|'[^'\n]*'|`[^`]*`/y],
        [
            "kw",
            /\b(?:const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|import|export|default|from|class|extends|new|this|typeof|instanceof|async|await|try|catch|finally|throw|null|undefined|true|false)\b/y,
        ],
        ["fn", /[A-Za-z_$][\w$]*(?=\s*\()/y],
        ["num", /\b\d+(?:\.\d+)?\b/y],
    ],
    python: [
        ["comment", /#[^\n]*/y],
        ["str", /"""[\s\S]*?"""|'''[\s\S]*?'''|"[^"\n]*"|'[^'\n]*'/y],
        [
            "kw",
            /\b(?:def|return|if|elif|else|for|while|in|not|and|or|import|from|as|class|try|except|finally|raise|with|lambda|pass|break|continue|True|False|None)\b/y,
        ],
        ["fn", /[A-Za-z_]\w*(?=\s*\()/y],
        ["num", /\b\d+(?:\.\d+)?\b/y],
    ],
    // SQL : les mots-clés s'écrivent aussi en minuscules (drapeau i), et un
    // texte ne s'écrit qu'entre guillemets simples, l'apostrophe doublée
    // restant dedans (`'l''atelier'`). Le guillemet double n'est pas colorié
    // en texte : SQLite le prend pour un nom de colonne, et le colorier comme
    // un texte confirmerait la mauvaise habitude que le correcteur refuse.
    // Une fonction colle sa parenthèse (`count(*)`) : `INSERT INTO clients
    // (nom)` ne doit pas peindre la table en fonction.
    sql: [
        ["comment", /--[^\n]*|\/\*[\s\S]*?\*\//y],
        ["str", /'(?:[^'\n]|'')*'/y],
        [
            "kw",
            /\b(?:select|from|where|and|or|not|as|order|by|asc|desc|limit|offset|distinct|insert|into|values|update|set|delete|create|table|drop|alter|add|column|primary|key|foreign|references|null|is|in|between|like|join|inner|left|right|full|outer|cross|on|using|group|having|union|all|except|intersect|case|when|then|else|end|exists|default|unique|check|integer|text|real|blob|numeric|true|false|with|index|view|if|constraint|autoincrement|collate|nocase|cast)\b/iy,
        ],
        ["fn", /[A-Za-z_]\w*(?=\()/y],
        ["num", /\b\d+(?:\.\d+)?\b/y],
    ],
};

// JSX = JavaScript plus les balises. Les balises passent AVANT le reste, sinon
// le nom du composant serait pris pour un appel de fonction.
LANGS.jsx = [
    ["tag", /<\/?[A-Za-z][\w.]*/y],
    ["tag", /\/?>/y],
    ["attr", /[a-zA-Z][\w-]*(?=\s*=)/y],
    ...LANGS.javascript,
];

// Langages IMBRIQUÉS : dans une page HTML, ce qui est entre <style> n'est pas
// du HTML mais du CSS, et ce qui est entre <script> est du JavaScript. Sans ça,
// une leçon qui montre une feuille de style dans sa page (le cas le plus
// courant du parcours) n'aurait de couleur que sur ses balises.
const EMBEDS = {
    html: [
        { open: /<style\b[^>]*>/y, close: "</style>", lang: "css" },
        { open: /<script\b[^>]*>/y, close: "</script>", lang: "javascript" },
    ],
};

const ALIASES = {
    js: "javascript",
    ts: "javascript",
    typescript: "javascript",
    tsx: "jsx",
    react: "jsx",
    py: "python",
    scss: "css",
};

export function normalizeLang(language) {
    const l = String(language || "").toLowerCase();
    const named = ALIASES[l] || l;
    return LANGS[named] ? named : "html";
}

/**
 * Découpe `src` en jetons { text, kind }. La concaténation des `text` redonne
 * EXACTEMENT `src` : c'est ce qui permet de retrouver un segment par son
 * décalage (voir sliceTokens). `kind` vaut null pour le texte ordinaire.
 */
export function tokenize(src, language) {
    return tokenizeWith(String(src == null ? "" : src), normalizeLang(language), true);
}

function tokenizeWith(code, langName, allowEmbeds) {
    const rules = LANGS[langName];
    const embeds = allowEmbeds ? EMBEDS[langName] || [] : [];
    const out = [];
    let plain = "";
    let i = 0;
    // Sommes-nous entre `<balise` et son `>` ? Seules les règles de portée
    // "tag" (attribut, valeur d'attribut) s'y appliquent.
    let inTag = false;
    const flush = () => {
        if (plain) {
            out.push({ text: plain, kind: null });
            plain = "";
        }
    };

    while (i < code.length) {
        // 1. Ouverture d'un langage imbriqué : la balise garde les couleurs de
        //    l'hôte, son contenu prend celles du langage imbriqué, et la balise
        //    fermante retombe dans la boucle normale.
        let embed = null;
        for (const e of embeds) {
            e.open.lastIndex = i;
            const m = e.open.exec(code);
            if (m && m[0]) {
                embed = { match: m[0], def: e };
                break;
            }
        }
        if (embed) {
            flush();
            out.push(...tokenizeWith(embed.match, langName, false));
            i += embed.match.length;
            const end = code.indexOf(embed.def.close, i);
            const stop = end === -1 ? code.length : end;
            out.push(...tokenizeWith(code.slice(i, stop), embed.def.lang, true));
            i = stop;
            continue;
        }

        // 2. Règles du langage courant.
        let hit = null;
        for (const rule of rules) {
            if (rule[3] === "tag" && !inTag) continue;
            const re = rule[1];
            re.lastIndex = i;
            const m = re.exec(code);
            if (m && m[0]) {
                hit = { text: m[0], kind: rule[2] || rule[0] };
                break;
            }
        }
        if (hit) {
            flush();
            out.push(hit);
            i += hit.text.length;
            // `<title` ouvre la balise, `>` et `/>` la referment. Une balise
            // fermante `</title` ouvre elle aussi : son `>` suit.
            if (hit.kind === "tag") inTag = hit.text.charAt(0) === "<";
        } else {
            plain += code[i];
            i += 1;
        }
    }
    flush();

    return out;
}

/**
 * Sous-suite de jetons couvrant [start, end) du code d'origine, en coupant les
 * jetons à cheval. Indispensable : on colorie le code ENTIER une fois, puis on
 * en distribue les morceaux aux lignes (find_error) ou aux segments cliquables
 * (tap_target, drop_blocks). Colorier chaque morceau isolément donnerait de
 * faux jetons, « font-size » seul n'ayant plus de deux-points derrière lui.
 */
export function sliceTokens(tokens, start, end) {
    const out = [];
    let pos = 0;
    for (const tk of tokens) {
        const tkStart = pos;
        const tkEnd = pos + tk.text.length;
        pos = tkEnd;
        if (tkEnd <= start) continue;
        if (tkStart >= end) break;
        const from = Math.max(start, tkStart) - tkStart;
        const to = Math.min(end, tkEnd) - tkStart;
        const text = tk.text.slice(from, to);
        if (text) out.push({ text, kind: tk.kind });
    }
    return out;
}

/**
 * Jetons de chaque LIGNE du code, la coloration étant calculée sur le code
 * entier (find_error, drop_blocks). Renvoie un tableau de tableaux, un par
 * ligne, sans les retours à la ligne.
 */
export function tokenizeLines(src, language) {
    const code = String(src == null ? "" : src);
    const tokens = tokenize(code, language);
    const out = [];
    let offset = 0;
    for (const line of code.split("\n")) {
        out.push(sliceTokens(tokens, offset, offset + line.length));
        offset += line.length + 1;
    }
    return out;
}

/**
 * Jetons de chaque SEGMENT d'un découpage auteur (tap_target : data.parts),
 * la coloration étant calculée sur leur concaténation.
 */
export function tokenizeParts(parts, language) {
    const list = Array.isArray(parts) ? parts.map((p) => String(p == null ? "" : p)) : [];
    const tokens = tokenize(list.join(""), language);
    const out = [];
    let offset = 0;
    for (const part of list) {
        out.push(sliceTokens(tokens, offset, offset + part.length));
        offset += part.length;
    }
    return out;
}

/**
 * Un extrait ressemble-t-il à du HTML ? (présence d'au moins une balise)
 */
export function looksLikeHtml(code) {
    return /<\/?[a-zA-Z][^>]*>/.test(code || "");
}

/**
 * Langue EFFECTIVE d'un bloc de code affiché : celle déclarée par le contenu
 * (data.language) si elle existe, sinon détection par balises, sinon la langue
 * du parcours.
 *
 * Cette fonction vit ici, à côté du tokenizer, parce qu'elle en fait partie :
 * tant qu'elle était dupliquée dans chaque player, le harnais de parité
 * comparait les deux tokenizers en leur passant LA MÊME langue, et ne pouvait
 * structurellement pas voir que les deux players ne choisissaient pas la même.
 * C'est ce trou qui a laissé le mobile jeter « css » et afficher du CSS en
 * gris uni sous une étiquette « index.html ».
 */
export function codeLang(code, dataLanguage, lang) {
    if (dataLanguage) return dataLanguage;
    // Parcours JavaScript : la langue du parcours passe AVANT le test des
    // chevrons. Du JavaScript en contient souvent (`"<p>Bonjour</p>"`,
    // `a < b`), et il serait pris pour du HTML. Un bloc de HTML dans ce
    // parcours déclare donc `data.language`.
    if (lang === "javascript") return "javascript";
    // Parcours SQL : même règle (`a < b`, `'<p>'` dans un texte).
    if (lang === "sql") return "sql";
    if (looksLikeHtml(code)) return "html";
    return lang === "python" ? "python" : "html";
}

/**
 * Nom affiché en tête d'un panneau de code, DÉDUIT DE LA LANGUE.
 *
 * Du CSS pur (règle isolée d'un tap_target) porté par « index.html » ferait
 * croire à l'apprenant qu'on écrit du CSS dans son HTML, ce que la leçon vient
 * justement de distinguer. Un panneau MONO-fichier de CSS, c'est toujours du
 * CSS posé dans la page, dans son bloc <style> : une leçon qui enseigne la
 * feuille à part passe par le multi-fichiers, qui affiche de vrais onglets
 * avec de vrais noms. Annoncer « styles.css » ici promettait un fichier que
 * l'apprenant ne connaît pas encore (l'unité 4 leçon 1 le repousse
 * explicitement à plus loin), et lui faisait croire qu'il en regarde un.
 */
export function fileNameFor(language) {
    if (language === "python") return "main.py";
    if (language === "javascript") return "script.js";
    if (language === "sql") return "query.sql";
    if (language === "css") return "<style>";
    return "index.html";
}

/**
 * Nom affiché en tête d'un panneau de code. `data.fileName` prime : une étape
 * qui parle de la page du menu ne peut pas annoncer « index.html », sinon
 * l'énoncé et l'écran se contredisent. Sans lui, on retombe sur le nom déduit
 * de la langue.
 *
 * Comme codeLang, cette paire vit ICI plutôt que dans chaque player : tant
 * qu'un panneau décidait seul de sa langue et de son nom, rien ne pouvait
 * signaler qu'il ne prenait pas la même décision que les autres. C'est ce trou
 * qui a laissé un concept de feuille de style s'afficher en Python, sous une
 * étiquette « main.py », alors que la leçon déclarait css / style.css.
 */
export function panelFileName(data, language) {
    const n =
        data && typeof data.fileName === "string" ? data.fileName.trim() : "";
    return n || fileNameFor(language);
}
